import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import AuthProvider from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import TimchehLoaderProvider from './components/TimchehLoaderProvider';
import Header from './components/Header';
import Footer from './components/Footer';
import MobileBottomNav from './components/MobileBottomNav';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import EmailActivationPage from './pages/EmailActivationPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdDetailsPage from './pages/AdDetailsPage';
import CreateAdPage from './pages/CreateAdPage';
import EditAdPage from './pages/EditAdPage';
import PaymentPage from './pages/PaymentPage';
import PlansPage from './pages/PlansPage';
import BusinessStorefrontPage from './pages/BusinessStorefrontPage';

import SearchPage from './pages/SearchPage';
import CategoryPage from './pages/CategoryPage';
import UserProfilePage from './pages/UserProfilePage';
import HelpPage from './pages/HelpPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import FAQPage from './pages/FAQPage';
import RulesPage from './pages/RulesPage';
import ProtectedRoute from './components/ProtectedRoute';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  // Cookie consent state
  const [showCookie, setShowCookie] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('cookieConsent')) {
      setShowCookie(true);
    }
  }, []);

  const handleAcceptCookie = () => {
    localStorage.setItem('cookieConsent', 'true');
    setShowCookie(false);
  };

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TimchehLoaderProvider>
          <AuthProvider>
            <Router>
            <div className="min-h-screen bg-gray-50 flex flex-col" dir="rtl" lang="fa-IR">
              <Header />
              <main className="flex-1 pb-24 md:pb-0">
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/activate-email" element={<EmailActivationPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/admin/login" element={<AdminLoginPage />} />
                  <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/category/:slug" element={<CategoryPage />} />
                  <Route path="/ad/:id" element={<AdDetailsPage />} />
                  <Route path="/ads/:slug" element={<AdDetailsPage />} />
                  <Route path="/user/:id" element={<UserProfilePage />} />
                  <Route path="/business/:slug/:businessId" element={<BusinessStorefrontPage />} />
                  <Route path="/business/:slug" element={<BusinessStorefrontPage />} />
                  <Route path="/business/:businessId" element={<BusinessStorefrontPage />} />
                  <Route path="/help" element={<HelpPage />} />
                  <Route path="/privacy-policy" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/terms-of-use" element={<TermsOfUsePage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/rules" element={<RulesPage />} />
                  <Route path="/payment" element={
                    <ProtectedRoute>
                      <PaymentPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/plans" element={
                    <ProtectedRoute>
                      <PlansPage />
                    </ProtectedRoute>
                  } />
                  
                  {/* Protected Routes */}
                  <Route path="/post-ad" element={
                    <ProtectedRoute>
                      <CreateAdPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/create-ad" element={
                    <ProtectedRoute>
                      <CreateAdPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/edit-ad/:id" element={
                    <ProtectedRoute>
                      <EditAdPage />
                    </ProtectedRoute>
                  } />
                  {/* Redirect old routes to profile with appropriate tabs */}
                  <Route path="/my-ads" element={
                    <ProtectedRoute>
                      <UserProfilePage />
                    </ProtectedRoute>
                  } />
                  <Route path="/favorites" element={
                    <ProtectedRoute>
                      <UserProfilePage />
                    </ProtectedRoute>
                  } />
                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <UserProfilePage />
                    </ProtectedRoute>
                  } />
                </Routes>
              </main>
              <Footer />
              <MobileBottomNav />
              <Toaster 
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                }}
              />
              {showCookie && (
                <div style={{ position: 'fixed', bottom: 0, right: 0, left: 0, zIndex: 1000 }} className="bg-gray-900 text-white px-4 py-3 flex flex-col md:flex-row items-center justify-between shadow-lg" dir="rtl">
                  <span className="mb-2 md:mb-0">وب‌سایت تیمچه از کوکی‌ها برای بهبود تجربه شما استفاده می‌کند.</span>
                  <button onClick={handleAcceptCookie} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg mr-4">متوجه شدم</button>
                </div>
              )}
            </div>
            </Router>
          </AuthProvider>
        </TimchehLoaderProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App; 
